/**
 * User data synchronization service
 * Handles checksum-based sync between GCS and IndexedDB
 */

import { auth, storage } from './firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import {
  getUserDataCache,
  storeUserDataCache,
  UserDataCache,
  clearUserData,
} from './indexedDB';

/**
 * Manifest structure (lightweight checksum file)
 */
interface UserDataManifest {
  version: string;
  checksum: string;
  generatedAt: string;
}

/**
 * Complete user data bundle from GCS
 */
interface UserDataBundle {
  version: string;
  checksum: string;
  profile: any;
  orders: any[];
  ownedProducts: any[];
  statistics: any;
}

/**
 * Sync user data with GCS bundle
 * Returns true if data was updated, false if already fresh
 */
export async function syncUserData(): Promise<boolean> {
  const user = auth.currentUser;

  if (!user) {
    console.log('[UserDataSync] No authenticated user, skipping sync');
    return false;
  }

  try {
    // 1. Fetch manifest (tiny file with checksum)
    console.log('[UserDataSync] Fetching manifest...');

    const manifestRef = ref(storage, `users/${user.uid}/manifest.json`);
    let manifestUrl: string;

    try {
      manifestUrl = await getDownloadURL(manifestRef);
    } catch (error: any) {
      // File doesn't exist yet (user has no orders/data)
      if (error.code === 'storage/object-not-found') {
        console.log('[UserDataSync] No user bundle exists yet (file not found)');
        return false;
      }
      throw error;
    }

    const manifestResponse = await fetch(manifestUrl);

    if (!manifestResponse.ok) {
      throw new Error(`Failed to fetch manifest: ${manifestResponse.status}`);
    }

    const manifest: UserDataManifest = await manifestResponse.json();

    // 2. Check cached checksum
    const cachedData = await getUserDataCache();
    const cachedChecksum = cachedData?.checksum;

    if (cachedChecksum === manifest.checksum) {
      console.log('✅ [UserDataSync] Data is fresh, using IndexedDB cache');
      return false; // No update needed
    }

    console.log('📥 [UserDataSync] Data changed, fetching update...');

    // 3. Fetch complete data bundle
    const dataRef = ref(storage, `users/${user.uid}/data.json`);
    const dataUrl = await getDownloadURL(dataRef);

    const dataResponse = await fetch(dataUrl);

    if (!dataResponse.ok) {
      throw new Error(`Failed to fetch data bundle: ${dataResponse.status}`);
    }

    const bundle: UserDataBundle = await dataResponse.json();

    // 4. Store in IndexedDB
    const cacheData: UserDataCache = {
      profile: bundle.profile,
      orders: bundle.orders,
      ownedProducts: bundle.ownedProducts,
      statistics: bundle.statistics,
      checksum: bundle.checksum,
      lastSync: Date.now(),
    };

    await storeUserDataCache(cacheData);

    console.log(`✅ [UserDataSync] Updated cache (${bundle.orders.length} orders, ${bundle.ownedProducts.length} products)`);

    return true; // Data was updated
  } catch (error) {
    console.error('[UserDataSync] Sync failed:', error);
    // If sync fails, we still have cached data - just use it
    return false;
  }
}

/**
 * Force refresh user data (ignores checksum)
 */
export async function forceRefreshUserData(): Promise<void> {
  const user = auth.currentUser;

  if (!user) {
    throw new Error('No authenticated user');
  }

  console.log('[UserDataSync] Force refreshing user data...');

  const dataRef = ref(storage, `users/${user.uid}/data.json`);
  const dataUrl = await getDownloadURL(dataRef);

  const dataResponse = await fetch(dataUrl);

  if (!dataResponse.ok) {
    throw new Error(`Failed to fetch data bundle: ${dataResponse.status}`);
  }

  const bundle: UserDataBundle = await dataResponse.json();

  const cacheData: UserDataCache = {
    profile: bundle.profile,
    orders: bundle.orders,
    ownedProducts: bundle.ownedProducts,
    statistics: bundle.statistics,
    checksum: bundle.checksum,
    lastSync: Date.now(),
  };

  await storeUserDataCache(cacheData);

  console.log('✅ [UserDataSync] Force refresh complete');
}

/**
 * Clear user data on logout
 */
export async function clearUserDataOnLogout(): Promise<void> {
  console.log('[UserDataSync] Clearing user data on logout...');
  await clearUserData();
}

/**
 * Initialize sync on app start
 * Should be called when user logs in or app initializes
 */
export async function initializeUserDataSync(): Promise<void> {
  const user = auth.currentUser;

  if (!user) {
    console.log('[UserDataSync] No user, skipping initialization');
    return;
  }

  console.log('[UserDataSync] Initializing user data sync...');

  // Check if we have cached data
  const cachedData = await getUserDataCache();

  if (cachedData && cachedData.orders.length > 0) {
    console.log('✅ [UserDataSync] Found cached data, UI can load instantly');
    // Sync in background to check for updates
    syncUserData().then(updated => {
      if (updated) {
        console.log('🔄 [UserDataSync] Background sync found updates');
      }
    });
  } else {
    console.log('[UserDataSync] No cached data, fetching from server...');
    // First time - fetch synchronously
    await syncUserData();
  }
}
